import React, { use, useMemo } from "react";
import { StyleSheet } from "react-native";
import { useQuery } from "@apollo/client/react";
import { StackScreenProps } from "@react-navigation/stack";
import ScreenCard from "../ScreenCard";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import Headline from "../../components/Headline";
import { BodyLargeText, BodyMediumText } from "../../components/FormattedText";
import ListItem from "../../ui_atoms/ListItem";
import HStack from "../../ui_atoms/HStack";
import VStack from "../../ui_atoms/VStack";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { ContractSignatureStatus, ProfileType, TerminationInfo } from "../../__generated__/graphql";
import { getMissionJobTitleDisplay } from "../../utils";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";

type MissionsMultiSignScreenProps = StackScreenProps<UserMissionsTabParamList, "MissionsMultiSign">;

const MissionsMultiSignScreen: React.FC<MissionsMultiSignScreenProps> = ({ route, navigation }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const { missionIds } = route.params;

    const { data, loading, error } = useQuery(FIND_MY_MISSIONS, { skip: !profileType });

    // Only the ids travel through the route — navigation params must stay serializable — so the
    // missions themselves are read back from the query the list screen already populated (the
    // default cache-first policy resolves them without a round trip). Contracts no longer
    // awaiting the current user's signature (e.g. signed elsewhere since selection) drop out.
    const missions = useMemo(
        () =>
            (data?.findMyMissions?.elements ?? []).filter(
                (m) =>
                    missionIds.includes(m.id) &&
                    !!m.currentContract?.signatureStatus &&
                    [
                        ContractSignatureStatus.NeedSelfSign,
                        ContractSignatureStatus.WaitingSelfSign,
                    ].includes(m.currentContract.signatureStatus),
            ),
        [data?.findMyMissions?.elements, missionIds],
    );

    const signButton = useMemo(
        () => (
            <Button
                key="sign"
                size="large"
                buttonMode="secondary"
                leftIcon="edit"
                leftIconSize={20}
                style={styles.signButton}
                disabled={missions.length === 0}
                label={i18n.t("screens.user.missions.sign_selected", { count: missions.length })}
                onPress={() => {
                    navigation.navigate("MissionsMultiSignProgress", {
                        missionIds: missions.map((mission) => mission.id),
                    });
                }}
            />
        ),
        [i18n, missions, navigation, styles.signButton],
    );

    if (loading && missions.length === 0) {
        return (
            <ScreenCard isContentContainerFlex>
                <Loading />
            </ScreenCard>
        );
    }

    return (
        <ScreenCard
            buttons={[signButton]}
            headband={
                error
                    ? buildErrorHeadband(i18n.t("screens.user.missions.cannot_load_missions"))
                    : undefined
            }
        >
            <VStack h="2/3" v="2/3">
                <BodyMediumText>
                    {i18n.t("screens.user.missions.multi_sign_intro", { count: missions.length })}
                </BodyMediumText>
            </VStack>
            <HStack h="2/3" v="2/3" fit="fill" alignment="center" wrapChildren={false}>
                <Headline h="4" style={styles.sectionTitle}>
                    {i18n.t("screens.user.missions.multi_sign_section")}
                </Headline>
                <BodyMediumText style={styles.sectionCount}>{missions.length}</BodyMediumText>
            </HStack>
            {missions.map((mission) => {
                const isClient = profileType === ProfileType.Client;
                const displayAttributes = getMissionDisplayAttributes(
                    mission,
                    profileType,
                    mission.terminationInfo as TerminationInfo,
                );
                const jobTitle = getMissionJobTitleDisplay(mission);
                const description = [jobTitle, displayAttributes.durationInfo]
                    .filter(Boolean)
                    .join(" · ");

                return (
                    <ListItem
                        key={mission.id}
                        useBigAvatarOrIcon
                        avatarPath={
                            isClient ? mission.workerInfo.avatarUrl : mission.clientInfo.logoUrl
                        }
                        title={isClient ? mission.workerInfo.name : mission.clientInfo.name}
                        description={description}
                        descriptionColor={colors.greyAccessible}
                        tags={
                            displayAttributes.salaryGross
                                ? [
                                      <BodyLargeText key="salary">
                                          {displayAttributes.salaryGross}
                                      </BodyLargeText>,
                                  ]
                                : undefined
                        }
                    />
                );
            })}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                sectionTitle: {
                    color: colors.greyAccessible,
                    letterSpacing: 1,
                },
                sectionCount: {
                    color: colors.greyAccessible,
                },
                signButton: {
                    alignSelf: "stretch",
                },
            }),
        [colors.greyAccessible],
    );
};

export default MissionsMultiSignScreen;
